/**
 * A room's label as the web draws it, the one piece of text the assembler
 * draws. Editor-core's room labels (assembler/roomLabels.ts) decide which
 * rooms wear one, what its lines say, where it centers and where a press on
 * it lands; this draws that layout as plain SVG, its lines set in the label
 * font and measured where the browser can measure them. The 2D plan, its
 * exports and the 3D view all draw this one label.
 *
 * The label itself never takes the pointer, so every gesture over it does
 * what it does anywhere else. A click on it selects its room: the 2D plan
 * puts a hit rect behind each backing (`roomLabelHitBox`), and the 3D view
 * tests the click with `roomLabelAt`.
 */

import { estimatedLineWidth, type LabelLineWidth, type PlacedRoomLabel, ROOM_LABEL_BACKING, roomLabelLayout } from '@kaiizen/kai-floor-plan-editor-core';
import { fBody, ink, midtoneDark, white } from '../../styles';

/** Where the plan and its exports center a room's label: its room's placed center. */
export { roomLabelCenter } from '@kaiizen/kai-floor-plan-editor-core';

let measuringContext: OffscreenCanvasRenderingContext2D | null | undefined;

/** How wide a line sets in the label's own font; core's estimate where there is no canvas to measure with. */
export const measuredLineWidth: LabelLineWidth = line => {
  measuringContext ??= typeof OffscreenCanvas === 'undefined' ? null : new OffscreenCanvas(1, 1).getContext('2d');
  if (!measuringContext) return estimatedLineWidth(line);
  measuringContext.font = `${line.bold ? 700 : 400} ${line.fontPx}px ${fBody}`;
  return measuringContext.measureText(line.text).width;
};

/**
 * The label centered on its `center`. Without a name, the size sits at the
 * center alone. Plain SVG attributes, no classes: the exports carry this
 * markup as is.
 */
export function RoomLabel(props: PlacedRoomLabel): React.JSX.Element | null {
  const layout = roomLabelLayout(props, measuredLineWidth);
  if (!layout) return null;
  const { box, lines, backingOpacity } = layout;
  return (
    <g data-layer='room-label' pointerEvents='none'>
      <rect {...box} rx={ROOM_LABEL_BACKING.radius} fill={white} fillOpacity={backingOpacity} />
      {lines.map(line => (
        <text
          key={line.kind}
          x={props.center.x}
          y={line.y}
          textAnchor='middle'
          dominantBaseline='central'
          data-room-size={line.kind === 'size' || undefined}
          style={{ fontFamily: fBody, fontWeight: line.bold ? 700 : 400, fontSize: `${line.fontPx}px`, fill: line.kind === 'size' ? midtoneDark : ink }}
        >
          {line.text}
        </text>
      ))}
    </g>
  );
}
